import { useToast, POSITION } from 'vue-toastification';
import ToastContent from '@/shared/ui/toast/ToastContent.vue';
import type { ToastContentProps } from '../types/toast.types';
import { ToastOptions } from 'vue-toastification/dist/types/types';

type CustomToastId = number | string;

export function useCustomToast(): {
  showSuccessToast: (
    contentProps: ToastContentProps,
    options?: Partial<ToastOptions>
  ) => CustomToastId;
  showErrorToast: (
    contentProps: ToastContentProps,
    options?: Partial<ToastOptions>
  ) => CustomToastId;
  showWarningToast: (
    contentProps: ToastContentProps,
    options?: Partial<ToastOptions>
  ) => CustomToastId;
  showValidationErrorToast: (validationErrors: { [key: string]: string[] }, title?: string) => void;
  dismissToast: (toastId: CustomToastId) => void;
} {
  const toast = useToast();

  const showSuccessToast = (
    contentProps: ToastContentProps,
    options: Partial<ToastOptions> = {}
  ): CustomToastId => {
    const toastClassName = options.toastClassName
      ? options.toastClassName + ' !pl-4 !w-full !rounded-2xl !border !border-green-19 !bg-green-xx'
      : '!pl-4 !w-full !rounded-2xl !border !border-green-19 !bg-green-xx';

    return toast.success(
      {
        component: ToastContent,
        props: contentProps,
      },
      {
        timeout: options.timeout ?? 4000,
        position: options.position ?? POSITION.BOTTOM_CENTER,
        toastClassName,
        closeButton: options.closeButton ?? true,
        icon: false,
        hideProgressBar: true,
        ...(options.bodyClassName !== undefined ? { bodyClassName: options.bodyClassName } : {}),
        ...(options.onClose !== undefined ? { onClose: options.onClose } : {}),
      }
    );
  };

  const showErrorToast = (
    contentProps: ToastContentProps,
    options: Partial<ToastOptions> = {}
  ): CustomToastId => {
    const toastClassName = options.toastClassName
      ? options.toastClassName + ' !pl-4 !w-full !rounded-2xl !border !border-red-16 !bg-pink-12'
      : '!pl-4 !w-full !rounded-2xl !border !border-red-16 !bg-pink-12';

    return toast.error(
      {
        component: ToastContent,
        props: contentProps,
      },
      {
        timeout: options.timeout ?? 6000,
        position: options.position ?? POSITION.BOTTOM_CENTER,
        toastClassName,
        closeButton: true,
        icon: false,
        hideProgressBar: true,
        ...(options.onClose !== undefined ? { onClose: options.onClose } : {}),
      }
    );
  };

  const showWarningToast = (
    contentProps: ToastContentProps,
    options: Partial<ToastOptions> = {}
  ): CustomToastId => {
    const toastClassName = options.toastClassName
      ? options.toastClassName + ' !pl-4 !w-full !rounded-2xl !border !border-nude !bg-beige-xx-02'
      : '!pl-4 !w-full !rounded-2xl !border !border-nude !bg-beige-xx-02';

    return toast.warning(
      {
        component: ToastContent,
        props: contentProps,
      },
      {
        timeout: options.timeout ?? 4000,
        position: options.position ?? POSITION.BOTTOM_CENTER,
        toastClassName,
        closeButton: true,
        icon: false,
        hideProgressBar: true,
        ...(options.onClose !== undefined ? { onClose: options.onClose } : {}),
      }
    );
  };

  const showValidationErrorToast = (
    validationErrors: { [key: string]: string[] },
    title = 'Erros no envio do documento'
  ): void => {
    const toastClassName = '!pl-4 !rounded-2xl !border !border-red-16 !bg-pink-12';

    toast.error(
      {
        component: ToastContent,
        props: {
          title,
          type: 'error',
          validationErrors,
        },
      },
      {
        timeout: 6000,
        position: POSITION.BOTTOM_CENTER,
        toastClassName,
        closeButton: true,
        icon: false,
        hideProgressBar: true,
      }
    );
  };

  const dismissToast = (toastId: CustomToastId): void => {
    toast.dismiss(toastId);
  };

  return {
    showSuccessToast,
    showErrorToast,
    showWarningToast,
    showValidationErrorToast,
    dismissToast,
  };
}
